<template>
  <div class="container">
    <header>
      <h1><i class="ri-bar-chart-2-line"></i> 学习统计</h1>
    </header>

    <!-- 快捷条 -->
    <div class="back-bar">
      <a class="back-link" @click="goHome"><i class="ri-arrow-left-line"></i> 返回列表</a>
      <a class="back-link" @click="goYear"><i class="ri-gift-line"></i> 年度总结</a>
      <a class="back-link" @click="goAbout"><i class="ri-user-line"></i> 关于我</a>
    </div>

    <div v-if="loading" class="loading">加载中…</div>
    <div v-else-if="error" class="error-tip">{{ error }}</div>
    <template v-else>
      <!-- 连续天数大字卡 -->
      <div class="streak-card">
        <div class="streak-main">
          <i class="ri-fire-line streak-fire"></i>
          <div class="streak-num">{{ data.streak_days || 0 }}</div>
          <div class="streak-label">当前连续学习（天）</div>
        </div>
        <div class="streak-side">
          <div class="streak-side-item">
            <div class="num">{{ data.max_streak || 0 }}</div>
            <div class="label">历史最长连续</div>
          </div>
          <div class="streak-side-item">
            <div class="num">{{ data.total_count || 0 }}</div>
            <div class="label">总篇数</div>
          </div>
          <div class="streak-side-item">
            <div class="num">{{ formatWords(data.total_words) }}</div>
            <div class="label">总字数</div>
          </div>
        </div>
      </div>

      <!-- 月度字数柱状 -->
      <div v-if="monthBars.length" class="dist-wrap">
        <div class="hm-title"><i class="ri-bar-chart-box-line"></i> 月度趋势 <span class="hm-sub">{{ data.year }} 年 · 每月字数</span></div>
        <div class="month-chart">
          <div v-for="m in monthBars" :key="m.month" class="month-col" :title="`${m.month} · ${m.count} 篇 · ${m.words} 字`">
            <div class="month-bar-bg">
              <div class="month-bar" :style="{ height: m.pct + '%' }"></div>
            </div>
            <div class="month-label">{{ m.label }}</div>
          </div>
        </div>
      </div>

      <!-- 星期分布 -->
      <div v-if="weekdayBars.length" class="dist-wrap">
        <div class="hm-title"><i class="ri-calendar-2-line"></i> 星期分布 <span class="hm-sub">哪几天最勤奋</span></div>
        <div v-for="w in weekdayBars" :key="w.name" class="dist-row">
          <span class="dist-name">{{ w.name }}</span>
          <div class="dist-bar-bg"><div class="dist-bar" :style="{ width: w.pct + '%' }"></div></div>
          <span class="dist-count">{{ w.count }}</span>
        </div>
      </div>

      <!-- 主题分布 -->
      <div v-if="topicBars.length" class="dist-wrap">
        <div class="hm-title"><i class="ri-price-tag-3-line"></i> 主题分布 <span class="hm-sub">{{ data.year }} 年 · {{ topicBars.length }} 个主题</span></div>
        <div v-for="t in topicBars" :key="t.topic_code" class="dist-row">
          <span class="dist-name" :title="t.topic_type || t.topic_code">{{ t.topic_type || t.topic_code }}</span>
          <div class="dist-bar-bg"><div class="dist-bar" :style="{ width: t.pct + '%' }"></div></div>
          <span class="dist-count">{{ t.count }}</span>
        </div>
      </div>

      <!-- 空态 -->
      <div v-if="!monthBars.length && !topicBars.length" class="empty">
        <i class="ri-inbox-line"></i> {{ data.year }} 年暂无学习记录
      </div>
    </template>
  </div>
</template>

<script>
import aiPush from '../api/aiPush'

const WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

export default {
  name: 'StatsPage',
  data () {
    return {
      loading: false,
      error: '',
      data: {}
    }
  },
  computed: {
    monthBars () {
      const list = this.data.month_words || []
      if (!list.length) return []
      const max = Math.max(...list.map(m => m.words || 0), 1)
      return list.map(m => ({
        month: m.month,
        words: m.words || 0,
        count: m.count || 0,
        label: (m.month || '').slice(5) + '月',
        pct: Math.max(4, Math.round(((m.words || 0) / max) * 100))
      }))
    },
    weekdayBars () {
      const arr = this.data.weekday_dist || []
      if (!arr.length) return []
      const max = Math.max(...arr, 1)
      // 按周一到周日展示（更符合中文习惯）
      const order = [1, 2, 3, 4, 5, 6, 0]
      return order.map(i => ({
        name: WEEKDAYS[i],
        count: arr[i] || 0,
        pct: Math.max(2, Math.round(((arr[i] || 0) / max) * 100))
      }))
    },
    topicBars () {
      const list = this.data.topic_dist || []
      if (!list.length) return []
      const max = Math.max(...list.map(t => t.count || 0), 1)
      return list.map(t => ({
        topic_code: t.topic_code,
        topic_type: t.topic_type,
        count: t.count || 0,
        pct: Math.max(2, Math.round(((t.count || 0) / max) * 100))
      }))
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      this.error = ''
      try {
        const { data } = await aiPush.stats()
        this.data = data || {}
      } catch (e) {
        this.error = e.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    formatWords (n) {
      if (!n) return '0'
      if (n >= 10000) return (n / 10000).toFixed(1) + 'w'
      return String(n)
    },
    goHome () {
      window.location.hash = '#/notes'
    },
    goYear () {
      window.location.hash = '#/year/' + (this.data.year || new Date().getFullYear())
    },
    goAbout () {
      window.location.hash = '#/about'
    }
  }
}
</script>
